import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getOrders, getAddresses, deleteAddress, updateAddress, updateUser,
  getMySecondHandItems, deleteSecondHandItem,
} from '../api';
import SecondHandCard from '../components/common/SecondHandCard';
import { FiPackage, FiMapPin, FiUser, FiEdit2, FiTrash2, FiStar, FiTag, FiPlus } from 'react-icons/fi';
import toast from 'react-hot-toast';
import './Dashboard.css';

const NPR = (n) => 'NPR ' + parseFloat(n || 0).toLocaleString('en-NP');

const ORDER_STATUS_BADGE = {
  pending: 'badge-yellow', processing: 'badge-blue', paid: 'badge-blue',
  shipped: 'badge-blue', delivered: 'badge-green', cancelled: 'badge-red',
};

const Dashboard = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'orders');
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [listings, setListings] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingAddrs, setLoadingAddrs] = useState(false);
  const [loadingListings, setLoadingListings] = useState(false);
  const [profileForm, setProfileForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (user) setProfileForm({
      first_name: user.first_name || '', last_name: user.last_name || '',
      email: user.email || '', phone: user.phone || '',
    });
  }, [user]);

  useEffect(() => {
    if (tab === 'orders') {
      setLoadingOrders(true);
      getOrders().then(({ data }) => setOrders(data.results || [])).finally(() => setLoadingOrders(false));
    }
    if (tab === 'addresses') {
      setLoadingAddrs(true);
      getAddresses().then(({ data }) => setAddresses(data.results || [])).finally(() => setLoadingAddrs(false));
    }
    if (tab === 'listings') {
      setLoadingListings(true);
      getMySecondHandItems()
        .then(({ data }) => setListings(data.results || data || []))
        .catch(() => toast.error('Could not load your listings'))
        .finally(() => setLoadingListings(false));
    }
  }, [tab]);

  // Deep-linkable tabs, e.g. /dashboard?tab=listings
  useEffect(() => {
    const urlTab = searchParams.get('tab') || 'orders';
    setTab((prev) => (urlTab === prev ? prev : urlTab));
  }, [searchParams]);

  const selectTab = (key) => {
    setTab(key);
    const p = new URLSearchParams(searchParams);
    if (key === 'orders') p.delete('tab'); else p.set('tab', key);
    setSearchParams(p, { replace: true });
  };

  const handleDeleteListing = async (slug) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      await deleteSecondHandItem(slug);
      setListings((prev) => prev.filter((l) => l.slug !== slug));
      toast.success('Listing deleted');
    } catch { toast.error('Could not delete listing'); }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try { await updateUser(user.id, profileForm); toast.success('Profile updated!'); }
    catch { toast.error('Update failed'); }
    finally { setSavingProfile(false); }
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm('Remove this address?')) return;
    try {
      await deleteAddress(id);
      setAddresses((prev) => prev.filter((a) => a.id !== id));
      toast.success('Address removed');
    } catch { toast.error('Failed to remove'); }
  };

  const handleSetDefault = async (id) => {
    try {
      await updateAddress(id, { is_default: true });
      setAddresses((prev) => prev.map((a) => ({ ...a, is_default: a.id === id })));
      toast.success('Default address updated');
    } catch { toast.error('Failed to update'); }
  };

  const TABS = [
    { key: 'orders',    label: 'My Orders',   icon: <FiPackage /> },
    { key: 'listings',  label: 'My Listings', icon: <FiTag /> },
    { key: 'addresses', label: 'Addresses',   icon: <FiMapPin /> },
    { key: 'profile',   label: 'Profile',     icon: <FiUser /> },
  ];

  return (
    <div className="dashboard-page">
      <div className="container">
        <div className="dashboard-header">
          <div>
            <h1 className="page-title">My Account</h1>
            <p className="text-muted">Welcome back, {user?.first_name || user?.username}</p>
          </div>
        </div>

        <div className="dashboard-layout">
          {/* Sidebar */}
          <div className="dash-sidebar">
            <div className="dash-user-card card">
              <div className="dash-avatar">{(user?.first_name || user?.username || 'U')[0].toUpperCase()}</div>
              <strong>{user?.first_name ? `${user.first_name} ${user.last_name}` : user?.username}</strong>
              <small className="text-muted">{user?.email}</small>
            </div>
            <nav className="dash-nav">
              {TABS.map((t) => (
                <button key={t.key} className={`dash-nav-item ${tab === t.key ? 'active' : ''}`} onClick={() => selectTab(t.key)}>
                  {t.icon} {t.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="dash-content">

            {/* ORDERS */}
            {tab === 'orders' && (
              <div className="card">
                <h2 className="section-title" style={{ marginBottom: 24 }}>My Orders</h2>
                {loadingOrders ? (
                  <div className="skeleton" style={{ height: 200 }} />
                ) : orders.length === 0 ? (
                  <div className="dash-empty">
                    <div className="dash-empty-icon">📦</div>
                    <p>No orders yet.</p>
                    <small className="text-muted">Your orders will appear here after checkout.</small>
                  </div>
                ) : (
                  <div className="table-wrapper">
                    <table>
                      <thead>
                        <tr>
                          <th>Order ID</th><th>Tracking ID</th><th>Total</th>
                          <th>Status</th><th>Date</th><th>Items Ordered</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((o) => (
                          <tr key={o.id}>
                            <td>#{o.id}</td>
                            <td><code style={{ fontSize: 12 }}>{o.tracking_id || '—'}</code></td>
                            <td style={{ fontWeight: 600 }}>{NPR(o.total)}</td>
                            <td><span className={`badge ${ORDER_STATUS_BADGE[o.status] || 'badge-gray'}`}>{o.status}</span></td>
                            <td>{new Date(o.created_at).toLocaleDateString('en-NP')}</td>
                            <td>
                              <div className="order-items-list">
                                {o.items?.length > 0 ? o.items.map((item) => (
                                  <div key={item.id} className="order-item-chip">
                                    <span className="order-item-qty">×{item.quantity}</span>
                                    <span className="order-item-name">
                                      {(item.second_hand_item || item.product)?.title || 'Product'}
                                      {item.second_hand_item && <em className="order-item-used"> (pre-owned)</em>}
                                    </span>
                                  </div>
                                )) : <span className="text-muted">—</span>}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* SECOND-HAND LISTINGS */}
            {tab === 'listings' && (
              <div className="card">
                <div className="dash-listings-header">
                  <div>
                    <h2 className="section-title">My Second-Hand Listings</h2>
                    <p className="text-muted">
                      Your listings stay private until an admin approves them — you can always see them here.
                    </p>
                  </div>
                  <Link to="/second-hand/sell" className="btn btn-primary btn-sm">
                    <FiPlus /> New Listing
                  </Link>
                </div>

                {loadingListings ? (
                  <div className="skeleton" style={{ height: 200 }} />
                ) : listings.length === 0 ? (
                  <div className="dash-empty">
                    <div className="dash-empty-icon">♻️</div>
                    <p>You have not listed anything yet.</p>
                    <small className="text-muted">Sell your used DJI gear to other pilots in Nepal.</small>
                    <Link to="/second-hand/sell" className="btn btn-primary" style={{ marginTop: 16 }}>
                      <FiPlus /> List an Item
                    </Link>
                  </div>
                ) : (
                  <div className="dash-listings-grid">
                    {listings.map((item) => (
                      <div key={item.id} className="dash-listing">
                        <SecondHandCard item={item} showStatus />
                        <div className="dash-listing-actions">
                          {!item.is_sold && (
                            <Link to={`/second-hand/${item.slug}/edit`} className="btn btn-outline btn-sm">
                              <FiEdit2 /> Edit
                            </Link>
                          )}
                          <button className="btn btn-ghost btn-sm dash-listing-delete"
                            onClick={() => handleDeleteListing(item.slug)}>
                            <FiTrash2 /> Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ADDRESSES */}
            {tab === 'addresses' && (
              <div className="card">
                <h2 className="section-title" style={{ marginBottom: 24 }}>Saved Addresses</h2>
                {loadingAddrs ? (
                  <div className="skeleton" style={{ height: 160 }} />
                ) : addresses.length === 0 ? (
                  <div className="dash-empty">
                    <div className="dash-empty-icon">📍</div>
                    <p>No saved addresses yet.</p>
                    <small className="text-muted">Addresses are saved automatically when you checkout.</small>
                  </div>
                ) : (
                  <div className="addr-grid">
                    {addresses.map((a) => (
                      <div key={a.id} className={`addr-card ${a.is_default ? 'default' : ''}`}>
                        <div className="addr-card-header">
                          {a.is_default
                            ? <span className="badge badge-red">Default</span>
                            : <span />
                          }
                          <div className="addr-card-actions">
                            {!a.is_default && (
                              <button className="addr-action-btn" title="Set as default" onClick={() => handleSetDefault(a.id)}>
                                <FiStar />
                              </button>
                            )}
                            <button className="addr-action-btn delete" title="Remove" onClick={() => handleDeleteAddress(a.id)}>
                              <FiTrash2 />
                            </button>
                          </div>
                        </div>
                        <div className="addr-card-body">
                          <p className="addr-line1">{a.line1}</p>
                          {a.line2 && <p>{a.line2}</p>}
                          <p>{a.city}, {a.state}</p>
                          {a.postal_code && <p>{a.postal_code}, {a.country}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* PROFILE */}
            {tab === 'profile' && (
              <div className="card">
                <h2 className="section-title" style={{ marginBottom: 24 }}>My Profile</h2>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label className="form-label">First Name</label>
                    <input value={profileForm.first_name} onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name</label>
                    <input value={profileForm.last_name} onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input type="email" value={profileForm.email} onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} />
                  </div>
                </div>
                <button className="btn btn-primary" style={{ marginTop: 20 }} onClick={saveProfile} disabled={savingProfile}>
                  <FiEdit2 /> {savingProfile ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;